import { normalizeDeviceLivePayload } from "./apiHelpers";

const toFiniteNumber = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableBoolean = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const lowered = value.trim().toLowerCase();
    if (["1", "true", "yes", "on", "acc_on", "ignition_on"].includes(lowered)) return true;
    if (["0", "false", "no", "off", "acc_off", "ignition_off"].includes(lowered)) return false;
  }
  return null;
};

const toIsoOrNull = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  if (!Number.isFinite(parsed)) return null;
  return new Date(parsed).toISOString();
};

const buildLegacyLikeRow = (row = {}) => {
  const id = row?._id || row?.deviceId || row?.id || null;
  // /devices/v2/page is backed by the aggregate list endpoint. Unlike the
  // slim v2 endpoint, its position can be present in liveData/currentLocation
  // or Mongo's GeoJSON field rather than top-level lat/lng. Dropping those
  // fallbacks made selected sidebar rows lose their map marker.
  const lat = toFiniteNumber(
    row?.lat ??
      row?.latitude ??
      row?.liveData?.lat ??
      row?.liveData?.latitude ??
      row?.currentLocation?.lat ??
      row?.currentLocation?.latitude ??
      row?.geoLocation?.coordinates?.[1] ??
      row?.location?.lat ??
      row?.location?.latitude,
    null
  );
  const lng = toFiniteNumber(
    row?.lng ??
      row?.longitude ??
      row?.liveData?.lng ??
      row?.liveData?.longitude ??
      row?.currentLocation?.lng ??
      row?.currentLocation?.longitude ??
      row?.geoLocation?.coordinates?.[0] ??
      row?.location?.lng ??
      row?.location?.longitude,
    null
  );
  // Aggregate list rows can include a stale top-level `speed: 0` alongside
  // the current tracker packet in `liveData`.  Live telemetry must win here:
  // Idle packets legitimately carry 1–5 km/h and must not be erased before
  // MapContainer or DeviceSidebar can render them.
  const speed = toFiniteNumber(
    row?.liveData?.speed ??
      row?.liveData?.currentSpeed ??
      row?.currentLocation?.speed ??
      row?.currentLocation?.currentSpeed ??
      row?.speed ??
      row?.currentSpeed ??
      row?.speedKmh ??
      row?.speed_kmh,
    0
  ) || 0;
  const heading = toFiniteNumber(
    row?.heading ?? row?.liveData?.heading ?? row?.currentLocation?.heading,
    0
  ) || 0;
  const ignition = toNullableBoolean(
    row?.ignition ?? row?.accStatus ?? row?.liveData?.ignition ?? row?.liveData?.accStatus ?? row?.currentLocation?.accStatus
  );
  const lastUpdate = toIsoOrNull(
    row?.lastUpdate ??
      row?.liveData?.serverReceivedAt ??
      row?.liveData?.serverTime ??
      row?.liveData?.correctedTime ??
      row?.liveData?.timestamp ??
      row?.currentLocation?.timestamp ??
      row?.lastDataReceivedAt
  );

  return normalizeDeviceLivePayload({
    _id: id,
    id,
    name: row?.name || "",
    vehicleNumber: row?.vehicleNumber || "",
    imei: row?.imei || "",
    msdn: row?.msdn || "",
    model: row?.model || "",
    protocol: row?.protocol || row?.protocolKey || "",
    vehicleType: row?.vehicleType || "",
    deviceType: row?.deviceType || "",
    deviceIcon: row?.deviceIcon || row?.vehicleType || "car",
    status: row?.status || "unknown",
    availabilityStatus: row?.availabilityStatus || row?.status || "unknown",
    movementStatus: row?.movementStatus || "unknown",
    vehicleStatus: row?.vehicleStatus ?? row?.liveData?.vehicleStatus ?? null,
    lat,
    lng,
    speed,
    heading,
    ignition,
    lastDataReceivedAt: toIsoOrNull(row?.lastDataReceivedAt),
    updatedAt: lastUpdate,
    lastUpdate,
    driverName: row?.driverName || "",
    driver: {
      driverName: row?.driverName || "",
    },
    deviceGroup: row?.deviceGroup || null,
    dealer: row?.dealer || null,
    assignedToUser: row?.assignedToUser || null,
    assignedToUsers: Array.isArray(row?.assignedToUsers) ? row.assignedToUsers : [],
    assignedToViewers: Array.isArray(row?.assignedToViewers) ? row.assignedToViewers : [],
    createdBy: row?.createdBy || null,
    ownerUser: row?.ownerUser || null,
    user: row?.user || null,
    customer: row?.customer || null,
    geofences: Array.isArray(row?.geofences) ? row.geofences : [],
    customFields: Array.isArray(row?.customFields) ? row.customFields : [],
    deviceLicense: row?.deviceLicense || null,
    licenseRuntime: row?.licenseRuntime || null,
    todaysOdo: row?.todaysOdo ?? null,
    todaysOdoRawKm: row?.todaysOdoRawKm ?? null,
    totalOdo: row?.totalOdo ?? null,
    totalOdoRawKm: row?.totalOdoRawKm ?? null,
    initialOdometer: row?.initialOdometer ?? null,
    initialOdometerRawKm: row?.initialOdometerRawKm ?? null,
    units: row?.units || null,
    liveData: {
      latitude: lat,
      longitude: lng,
      lat,
      lng,
      speed,
      heading,
      accStatus: ignition,
      ignition,
      vehicleStatus: row?.vehicleStatus ?? row?.liveData?.vehicleStatus ?? null,
      timestamp: lastUpdate,
      lastUpdate,
    },
    currentLocation:
      lat !== null && lng !== null
        ? {
            latitude: lat,
            longitude: lng,
            speed,
            heading,
            accStatus: ignition,
            ignition,
            vehicleStatus: row?.vehicleStatus ?? row?.liveData?.vehicleStatus ?? null,
            timestamp: lastUpdate,
          }
        : null,
    geoLocation:
      lat !== null && lng !== null
        ? {
            lat,
            lng,
          }
        : null,
  });
};

export const adaptV2DeviceRow = (row = {}) => buildLegacyLikeRow(row);

export const adaptV2DeviceRows = (rows = []) =>
  Array.isArray(rows) ? rows.map((row) => adaptV2DeviceRow(row)).filter(Boolean) : [];

export const adaptV2DeviceDetail = (payload = {}) => normalizeDeviceLivePayload(payload || {});

export const adaptV2LatestStatus = (payload = {}) => buildLegacyLikeRow(payload);

export const patchLegacyDeviceWithV2Delta = (device = {}, delta = {}) => {
  if (!device || !delta) return device;

  const lastUpdate = toIsoOrNull(delta?.lastUpdate || delta?.lastDataReceivedAt) || device?.lastDataReceivedAt || null;
  const ignition = toNullableBoolean(
    delta?.ignition ?? delta?.accStatus ?? device?.ignition ?? device?.liveData?.ignition ?? device?.liveData?.accStatus
  );
  const lat = toFiniteNumber(delta?.lat, device?.currentLocation?.latitude ?? device?.liveData?.latitude ?? null);
  const lng = toFiniteNumber(delta?.lng, device?.currentLocation?.longitude ?? device?.liveData?.longitude ?? null);

  return normalizeDeviceLivePayload({
    ...device,
    status: delta?.status ?? device?.status,
    availabilityStatus: delta?.availabilityStatus ?? device?.availabilityStatus,
    movementStatus: delta?.movementStatus ?? device?.movementStatus,
    vehicleStatus: delta?.vehicleStatus ?? device?.liveData?.vehicleStatus ?? device?.vehicleStatus,
    ignition,
    lastDataReceivedAt: toIsoOrNull(delta?.lastDataReceivedAt) || device?.lastDataReceivedAt || null,
    updatedAt: lastUpdate,
    liveData: {
      ...(device?.liveData || {}),
      latitude: lat,
      longitude: lng,
      lat,
      lng,
      speed: toFiniteNumber(
        delta?.liveData?.speed ??
          delta?.currentLocation?.speed ??
          delta?.speed ??
          delta?.currentSpeed ??
          delta?.speedKmh ??
          delta?.speed_kmh,
        device?.liveData?.speed ?? 0
      ) || 0,
      heading: toFiniteNumber(delta?.heading, device?.liveData?.heading ?? 0) || 0,
      accStatus: ignition,
      ignition,
      vehicleStatus: delta?.vehicleStatus ?? device?.liveData?.vehicleStatus ?? device?.vehicleStatus ?? null,
      timestamp: lastUpdate,
      lastUpdate,
    },
    currentLocation:
      lat !== null && lng !== null
        ? {
            ...(device?.currentLocation || {}),
            latitude: lat,
            longitude: lng,
            speed: toFiniteNumber(
              delta?.liveData?.speed ??
                delta?.currentLocation?.speed ??
                delta?.speed ??
                delta?.currentSpeed ??
                delta?.speedKmh ??
                delta?.speed_kmh,
              device?.currentLocation?.speed ?? 0
            ) || 0,
            heading: toFiniteNumber(delta?.heading, device?.currentLocation?.heading ?? 0) || 0,
            accStatus: ignition,
            ignition,
            vehicleStatus: delta?.vehicleStatus ?? device?.liveData?.vehicleStatus ?? device?.vehicleStatus ?? null,
            timestamp: lastUpdate,
          }
        : device?.currentLocation || null,
    geoLocation:
      lat !== null && lng !== null
        ? {
            lat,
            lng,
          }
        : device?.geoLocation || null,
  });
};

export const mergeLegacyDeviceDetail = (baseDevice = {}, detailDevice = {}) =>
  normalizeDeviceLivePayload({
    ...(baseDevice || {}),
    ...(detailDevice || {}),
    driver: {
      ...(baseDevice?.driver || {}),
      ...(detailDevice?.driver || {}),
    },
    deviceLicense:
      detailDevice?.deviceLicense !== undefined
        ? detailDevice.deviceLicense
        : baseDevice?.deviceLicense ?? null,
    geofences: Array.isArray(detailDevice?.geofences)
      ? detailDevice.geofences
      : Array.isArray(baseDevice?.geofences)
        ? baseDevice.geofences
        : [],
    customFields: Array.isArray(detailDevice?.customFields)
      ? detailDevice.customFields
      : Array.isArray(baseDevice?.customFields)
        ? baseDevice.customFields
        : [],
  });
